import React, { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import {
    Col,
    Container,
    Row,
    TabContent,
    TabPane,
} from "reactstrap"
import MerchantForm from "./merchant-form"
import MerchantBankingForm from "./merchant-banking-form"
import MerchantCommunicationForm from "./communication-form"
import { PageTitle } from "constants/ui"
import MerchantContactForm from "./contact-form"
import MerchantTimingsForm from "./merchant-timings-form"
import CButton from "components/Common/CButton"
import { getUserAuthorized } from "helpers/backend_helper"
import PermissionTabs from "components/Common/PermissionTabs"
import MerchantImageForm from "./images-form"
import { getTabNames, getTabPermissions, isTabMandatory } from "helpers/tab_helper"
import MerchantAttributeForm from "./attribute-form"
import MerchantTagsForm from "./merchant-tags-form"
import MerchantVariablesForm from "./variables-form"
import MerchantPinsForm from "./merchant-pins-form"

const MerchantDetail = props => {
    const { match } = props
    const page_title = match?.params?.id
        ? PageTitle["MERCHANT_DETAIL"]
        : PageTitle["MERCHANT_ADD"]

    const page_type = match?.params?.id ? "detail" : "add"

    const parent_page_title = PageTitle.MERCHANT_LIST
    document.title = page_title

    const [activeTab, setactiveTab] = useState("1")
    // // Only add tab flow state for create mode
    const [completedTabs, setCompletedTabs] = useState(
        page_type === "detail" ? ["1", "2", "3", "4"] : []
    )
    const [enabledTabs, setEnabledTabs] = useState(
        page_type === "detail" ? ["1", "2", "3", "4"] : ["1"]
    ) // Track which tabs are enabled
    const [merchantCreated, setMerchantCreated] = useState(
        page_type === "detail"
    )
    const [createdMerchantID, setCreatedMerchantID] = useState(null)

    // Permission checking state
    const [tabPermissions, setTabPermissions] = useState({})
    const [isCheckingPermission, setIsCheckingPermission] = useState(false)
    const [permissionErrors, setPermissionErrors] = useState({})

    // Banking form finish tracking
    const [finishRequested, setFinishRequested] = useState(false)
    const [previousBankingState, setPreviousBankingState] = useState(null)

    // State to track legal entity info for breadcrumb
    const [legalEntityInfo, setLegalEntityInfo] = useState({
        legalEntityName: "",
        legalEntityAddress: ""
    })

    // Get communications, contacts, timings, banking data, and merchantDetail from Redux
    const {
        merchantCommunications,
        merchantContacts,
        merchantTimings,
        merchantBanking,
        merchantAttributes,
        merchantTags,
        bankingError,
        userMenu,
        merchantVariables,
        merchantImages,
        merchantPins,
        merchantDetail
    } = useSelector(state => ({
        merchantCommunications:
            state.merchants.merchantCommunications || [],
        merchantContacts: state.merchants.merchantContacts || [],
        merchantTimings: state.merchants.merchantTimings || [],
        merchantAttributes: state.merchants.merchantAttributes || [],
        merchantTags: state.merchants.merchantTags || [],
        merchantBanking: state.merchants.merchantBanking || [],
        merchantImages: state.merchants.merchantImages || [],
        merchantVariables: state.merchants.merchantVariables,
        merchantPins: state.merchants.merchantPins,
        bankingError: state.merchants.bankingError,
        userMenu: state.userMenu.userMenu || [],
        merchantDetail: state.merchants.merchantDetail
    }))

    const pageKey = "MERCHANT_DETAIL"
    const tabNames = getTabNames(pageKey, userMenu)
    const tabPermissionsArray = getTabPermissions(pageKey, page_type, userMenu)

    // Generic form state checker
    const hasFormContent = (tabName) => {
        switch (tabName.toLowerCase()) {
            case 'merchant':
                return merchantCreated
            case 'communications':
                return merchantCommunications.length > 0
            case 'contacts':
                return merchantContacts.length > 0
            case 'timings':
                return merchantTimings.length > 0
            case 'attributes':
                return merchantAttributes.length > 0
            case 'tags':
                return merchantTags.length > 0
            case 'images':
                return merchantImages.length > 0
            case 'variables':
                return merchantVariables !== null
            case 'merchant pins':
                return merchantPins !== null
            case 'bankings':
                return merchantBanking.length > 0
            default:
                return false
        }
    }


    // Permission checking function with API failure handling
    const checkTabPermission = async (tabId, forceRefresh = false) => {
        // Skip permission check for detail mode
        if (page_type === "detail") {
            return true
        }

        // Use cached permission if available and not forcing refresh
        if (!forceRefresh && tabPermissions[tabId] !== undefined) {
            return tabPermissions[tabId]
        }

        const permission = tabPermissionsArray[tabId - 1]
        if (!permission) {
            return true
        }

        try {
            setIsCheckingPermission(true)
            setPermissionErrors(prev => ({ ...prev, [tabId]: null }))

            const result = await getUserAuthorized({
                headers: {
                    "input-parameters": {
                        requestURI: permission.contextUri,
                        requestMethod: permission.actionType,
                    },
                },
            })

            // Check if permission is granted based on API response
            const hasPermission =
                result?.type === "USER_AUTHORIZATION_STATUS" &&
                result?.desc === "true" &&
                result?.code === "200"

            setTabPermissions(prev => ({ ...prev, [tabId]: hasPermission }))
            return hasPermission
        } catch (error) {
            console.error(`Permission check failed for tab ${tabId}:`, error)

            // Handle API failure - default to deny for security but mark as error
            setPermissionErrors(prev => ({
                ...prev,
                [tabId]: {
                    message: "Unable to verify permissions. Please try again.",
                    isApiError: true,
                },
            }))
            setTabPermissions(prev => ({ ...prev, [tabId]: false }))
            return false
        } finally {
            setIsCheckingPermission(false)
        }
    }

    // Check permission for merchant tab on page load
    useEffect(() => {
        if (page_type === "add") {
            checkTabPermission(1)
        }
    }, [page_type])

    // Update legal entity info from Redux in detail mode
    useEffect(() => {
        if (page_type === "detail" && merchantDetail) {
            const merchant = Array.isArray(merchantDetail)
                ? merchantDetail.find(m => m.merchantID === match?.params?.id)
                : merchantDetail

            if (merchant) {
                setLegalEntityInfo({
                    legalEntityName: merchant.legalEntityName || "",
                    legalEntityAddress: merchant.legalEntityAddress || ""
                })
            }
        }
    }, [page_type, merchantDetail, match?.params?.id])

    // Monitor banking form success when finish is requested
    useEffect(() => {
        if (finishRequested && merchantBanking && !bankingError) {
            // Banking form was successfully submitted after finish was requested
            if (merchantBanking !== previousBankingState) {
                setFinishRequested(false)
                setPreviousBankingState(merchantBanking)

                // Navigate to merchant detail page
                if (createdMerchantID) {
                    props.history.push(`/merchants/${createdMerchantID}`)
                } else {
                    props.history.push("/merchants")
                }
            }
        } else if (finishRequested && bankingError) {
            // Banking form submission failed - reset flag but don't navigate
            setFinishRequested(false)
        }
    }, [
        finishRequested,
        merchantBanking,
        bankingError,
        previousBankingState,
        createdMerchantID,
        props.history,
    ])

    const toggle = tab => {
        // For detail mode, keep original behavior
        if (page_type === "detail") {
            if (activeTab !== tab) {
                setactiveTab(tab)
            }
            return
        }

        // For create mode, only allow navigation to enabled tabs
        if (enabledTabs.includes(tab)) {
            setactiveTab(tab)
        }
    }

    const handleMerchantCreation = merchantID => {
        setMerchantCreated(true)
        setCreatedMerchantID(merchantID)
        setCompletedTabs(prev => (prev.includes("1") ? prev : [...prev, "1"]))
        // Enable the next tab (Communication) after merchant creation
        setEnabledTabs(prev => {
            if (!prev.includes("2")) {
                return [...prev, "2"]
            }
            return prev
        })
    }

    const handleTabCompletion = tabId => {
        if (!completedTabs.includes(tabId)) {
            setCompletedTabs(prev => [...prev, tabId])
        }
    }

    // Handler for form changes (called from merchant-form)
    const handleFormChange = (formValues) => {
        if (formValues) {
            setLegalEntityInfo({
                legalEntityName: formValues.legalEntityName || "",
                legalEntityAddress: formValues.legalEntityAddress || ""
            })
        }
    }

    const navigateToNextTab = async () => {
        const currentTabIndex = parseInt(activeTab)
        const nextTab = String(currentTabIndex + 1)
        if (currentTabIndex < tabNames.length) {
            // Enable the next tab when navigating
            setEnabledTabs(prev => {
                if (!prev.includes(nextTab)) {
                    return [...prev, nextTab]
                }
                return prev
            })

            // Check permission for next tab on forward navigation (force refresh)
            if (page_type === "add" && tabPermissionsArray[parseInt(nextTab) - 1]) {
                await checkTabPermission(parseInt(nextTab), true)
            }

            setactiveTab(nextTab)
        }
    }

    const navigateToPreviousTab = () => {
        const currentTabIndex = parseInt(activeTab)
        const previousTab = String(currentTabIndex - 1)
        if (currentTabIndex > 1) {
            setactiveTab(previousTab)
        }
    }

    const isTabDisabled = tabIndex => {
        if (page_type === "detail") return false // All tabs enabled for detail/edit mode
        return !enabledTabs.includes(String(tabIndex)) // Tab is disabled if not in enabledTabs
    }

    // No Access Message Component
    const NoAccessMessage = ({ tabId, onRetry }) => {
        const permissionError = permissionErrors[tabId]
        const isApiError = permissionError?.isApiError

        return (
            <div className="text-center p-5">
                <i
                    className={`display-4 mb-3 ${isApiError
                        ? "mdi mdi-wifi-off text-warning"
                        : "mdi mdi-lock-outline text-muted"
                        }`}
                ></i>
                <h5>{isApiError ? "Connection Error" : "Access Restricted"}</h5>
                <p className="text-muted">
                    {isApiError
                        ? "Unable to verify permissions. Please try again."
                        : "You do not have access to this module. Contact your administrator for access rights."}
                </p>
            </div>
        )
    }

    const renderTabContent = (tabName, tabIndex) => {
        switch (tabName.toLowerCase()) {
            case 'merchant':
                return (
                    <MerchantForm
                        page={page_type}
                        {...props}
                        onMerchantCreated={handleMerchantCreation}
                        onTabCompletion={handleTabCompletion}
                        navigateToNextTab={navigateToNextTab}
                        activeTab={activeTab}
                        onFormChange={handleFormChange}
                    />
                )

            case 'communications':
                return (
                    <MerchantCommunicationForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'contacts':
                return (
                    <MerchantContactForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'timings':
                return (
                    <MerchantTimingsForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'tags':
                return (
                    <MerchantTagsForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'images':
                return (
                    <MerchantImageForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'attributes':
                return (
                    <MerchantAttributeForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'variables':
                return (
                    <MerchantVariablesForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'merchant pins':
                return (
                    <MerchantPinsForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdMerchantID={createdMerchantID}
                    />
                )

            case 'bankings':
                return (
                    <MerchantBankingForm
                        page={page_type}
                        {...props}
                        onTabCompletion={handleTabCompletion}
                        activeTab={activeTab}
                        tabId={String(tabIndex)}
                        createdEntityID={createdMerchantID}
                    />
                )

            default:
                return (
                    <div className="text-center p-5">
                        <h5>Tab content not implemented</h5>
                        <p className="text-muted">Content for "{tabName}" tab is not yet implemented.</p>
                    </div>
                )
        }
    }

    const handleFinish = () => {
        if (createdMerchantID) {
            props.history.push(`/merchants/${createdMerchantID}`)
        } else {
            props.history.push("/merchants")
        }
    }

    const handleSkipFinish = () => {
        props.history.push("/merchants")
    }

    const renderNavigationButtons = (tabName, tabIndex) => {
        const isLastTab = tabIndex >= tabNames.length
        const isMandatory = isTabMandatory(pageKey, tabName, userMenu)
        const hasContent = hasFormContent(tabName)

        // Last tab - special finish logic with mandatory check
        if (isLastTab) {
            if (isMandatory) {
                return hasContent ?
                    <CButton.Primary text="Finish" onClick={handleFinish} disabled={isCheckingPermission} /> :
                    false
            } else {
                return hasContent ?
                    <CButton.Primary text="Finish" onClick={handleFinish} disabled={isCheckingPermission} /> :
                    <CButton.Secondary text="Skip & Finish" onClick={handleSkipFinish} disabled={isCheckingPermission} />
            }
        }

        // All other tabs (first, middle) - unified logic
        if (isMandatory) {
            return hasContent ?
                <CButton.Primary text="Continue" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} /> : false

        } else {
            return hasContent ?
                <CButton.Primary text="Continue" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} /> :
                <CButton.Secondary text="Skip" onClick={navigateToNextTab} disabled={isCheckingPermission || isLastTab} />
        }
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid={true}>
                    <Breadcrumbs
                        titleLink="/merchants"
                        title={parent_page_title}
                        breadcrumbItem={page_title}
                        secondaryInfo={[
                            { label: "Legal Entity Name", value: legalEntityInfo.legalEntityName },
                            { label: "Legal Entity Address", value: legalEntityInfo.legalEntityAddress }
                        ]}
                    />
                    <Row>
                        <Col md="12">
                            <PermissionTabs
                                tabs={tabNames}
                                activeTab={activeTab}
                                onTabChange={toggle}
                                isTabDisabled={isTabDisabled}
                                tabPermissions={tabPermissionsArray}
                                history={props.history}
                            />

                            <TabContent activeTab={activeTab} className="p-3 text-muted">
                                {tabNames.map((tabName, index) => {
                                    const tabIndex = index + 1
                                    const tabId = String(tabIndex)

                                    return (
                                        <TabPane key={tabId} tabId={tabId}>
                                            {tabPermissions[tabIndex] === false ? (
                                                <NoAccessMessage tabId={tabIndex} />
                                            ) : (
                                                renderTabContent(tabName, tabIndex)
                                            )}

                                            {/* Navigation buttons for add mode */}
                                            {page_type === "add" && tabPermissions[tabIndex] !== false && (
                                                <div className="mt-3 d-flex justify-content-between">
                                                    <CButton.Secondary
                                                        text={tabIndex === 1 ? "Cancel" : "Back"}
                                                        onClick={() =>
                                                            tabIndex === 1
                                                                ? props.history.push("/merchants")
                                                                : navigateToPreviousTab()
                                                        }
                                                        disabled={tabIndex === 1 ? false : parseInt(activeTab) <= 1}
                                                    />
                                                    <div>
                                                        {renderNavigationButtons(tabName, tabIndex)}
                                                    </div>
                                                </div>
                                            )}
                                        </TabPane>
                                    )
                                })}
                            </TabContent>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    )
}

export default MerchantDetail